import { FC, useEffect, useState } from 'react';
import { Box, Button, Grid, styled, Typography } from '@mui/material';

import { useAppDispatch, useAppSelector } from 'state/hooks';
import { icons, images, tokens } from 'config/constants/assets';
import { TokenCard } from 'components/Overview';
import { TokenInfo } from 'types/token';
import { formatNumber } from 'utils/formatNumber';
import TvlCard from 'components/TvlCard';
import { OverviewOptions } from 'components/Overview/OverviewOptions';
import { LiquidityInfo } from 'types/liquidity';
import LiquidityCard from 'components/Overview/LiquidityCard';
import ProtocolChains from 'components/Protocols/ProtocolChains';
import TutorialModal from 'components/Modal/TutorialModal';
import "./marque.css"
import { fetchLiquidityGlobalDataAsync, fetchTokenGlobalDataAsync } from 'state/actions';

const Container = styled(Box)(({ theme }) => ({
  position: 'relative',
  width: '100%',
  minHeight: 'calc(100vh - 194px)',
  padding: '0px 24px',
  paddingTop: '19px',

  [theme.breakpoints.up('xs')]: {
    minHeight: 'calc(100vh - 284px)',
  },
  [theme.breakpoints.up('md')]: {
    minHeight: 'calc(100vh - 194px)',
  },
}));

const Banner = styled(Box)(({ theme }) => ({
  backgroundImage: `url(${images.overviewBanner})`,
  backgroundSize: 'cover',
  width: '100%',
  height: '255px',
  maxHeight: '255px',
  position: 'absolute',
  top: '0px',
  left: '0px',
  right: '0px',

  [theme.breakpoints.up('xs')]: {
    height: '200px',
  },
  [theme.breakpoints.up('md')]: {
    height: '225px',
  },
  [theme.breakpoints.up('lg')]: {
    height: '255px',
  },
}));

const BannerHero = styled(Box)(({ theme }) => ({
  backgroundImage: `url(${images.overviewBannerHero})`,
  backgroundSize: 'cover',
  width: '185px',
  height: '255px',
  maxHeight: '255px',
  position: 'absolute',
  top: '0px',
  right: '15%',

  [theme.breakpoints.up('xs')]: {
    height: '200px',
    right: '10%'
  },
  [theme.breakpoints.up('md')]: {
    height: '225px',
    right: '10%',
  },
  [theme.breakpoints.up('lg')]: {
    height: '255px',
    right: '8%',
  },
  [theme.breakpoints.up('xl')]: {
    height: '255px',
    right: '25%',
  },
  "@media (min-width:1920px) and (max-width:2500px)": {    
    height: '255px',
    right: '18%',
  },
}));

const BottomBanner = styled(Box)(({ theme }) => ({
  backgroundImage: `url(${images.bottomBanner})`,
  backgroundSize: 'cover',
  width: '100%',
  height: '160px',
  maxHeight: '255px',
  position: 'absolute',
  bottom: '0px',
  right: '0%',
  left: '0%',
}));

const Content = styled(Box)(() => ({
  position: 'relative',
  maxWidth: '851px',
  margin: 'auto',
}));

// overview section
const OverviewSection = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: '20px',
  maxWidth: '100%',

  [theme.breakpoints.up('xs')]: {
    flexDirection: 'column',
    overflow: 'visible',
  },
  [theme.breakpoints.up('sm')]: {
    flexDirection: 'row',
    overflowX: 'auto',
  },
}));

// burn box
const BurnBox = styled(Box)(({ theme }) => ({
  display: 'block',
  flexDirection: 'column',
  background: theme.palette.background.box,
  height: '100%',
  minWidth: '270px',
  position: 'relative',

  [theme.breakpoints.up('xs')]: {
    borderRadius: '20px',
    padding: '8px 17px',
  },
  [theme.breakpoints.up('sm')]: {
    borderRadius: '8px',
    padding: '18px 17px',
  },
}));

const BurnBoxTitle = styled(Typography)(({ theme }) => ({
  fontFamily: 'Prompt',
  fontStyle: 'normal',
  fontWeight: '600',
  color: theme.palette.text.primary,
  fontSize: '17px',
  lineHeight: '26px',

  [theme.breakpoints.up('xs')]: {
    marginBottom: '5px',
  },
  [theme.breakpoints.up('sm')]: {
    marginBottom: '18px',
  },
}));

const TokenBurnCardBasedBack = styled(Box)(({ theme }) => ({
  backgroundImage: `url(${images.tokenBurnCardBased})`,
  backgroundSize: 'cover',
  width: '120px',
  height: '90px',
  maxHeight: '255px',
  position: 'absolute',
  right: '0%',
  bottom: '-6px',
  [theme.breakpoints.up('xs')]: {
    right: '49px',
  },
  [theme.breakpoints.up('md')]: {
    right: '0%',
  },
}));

const TokenBurnCardBshareBack = styled(Box)(({ theme }) => ({
  backgroundImage: `url(${images.tokenBurnCardBshare})`,
  backgroundSize: 'cover',
  width: '120px',
  height: '90px',
  maxHeight: '255px',
  position: 'absolute',
  left: '0%',
  top: '-6px',
  [theme.breakpoints.up('xs')]: {
    left: '49px',
  },
  [theme.breakpoints.up('md')]: {
    left: '0%',
  },
}));

const TokenBoilerRoomCardBack = styled(Box)(({ theme }) => ({
  backgroundImage: `url(${images.tokenBoilerRoomCard})`,
  backgroundSize: 'cover',
  width: '100%',
  height: '40px',
  maxHeight: '255px',
  position: 'absolute',
  left: '0%',
  bottom: '10px',
  [theme.breakpoints.up('xs')]: {
    height: '0px'
  },
  [theme.breakpoints.up('md')]: {
    height: '40px',
  },
}));

const TokenBurnCard = styled(Box)(({ theme }) => ({
  borderRadius: '10px',
  background: '#CDC392',
  height: '100%',
  padding: '15px 8px',
  display: 'flex',
  alignItems: 'center',

  [theme.breakpoints.up('xs')]: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    padding: '4px 24px',
  },
  [theme.breakpoints.up('sm')]: {
    flexDirection: 'column',
    padding: '15px 8px',
  },
}));

const TokenBurnCardImg = styled('img')(({ theme }) => ({
  height: '70px',
  display: 'block',

  [theme.breakpoints.up('xs')]: {
    height: '50px',
  },
  [theme.breakpoints.up('sm')]: {
    height: '70px',
  },
}));

const TokenBurnAmount = styled(Typography)(({ theme }) => ({
  fontFamily: 'Prompt',
  fontStyle: 'normal',
  fontWeight: '600',
  color: 'rgba(39, 40, 42, 0.8)',
  fontSize: '24px',
  lineHeight: '28px',
  textAlign: 'center',
  zIndex: 1 ,
  [theme.breakpoints.up('xs')]: {
    marginTop: '0px',
  },
  [theme.breakpoints.up('sm')]: {
    marginTop: '8px',
  },
}));

// token list section
const TokenListSection = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '10px',
  marginTop: '20px',
}));

// toggle section
const ToggleSection = styled(Box)(() => ({
  position: 'absolute',
  right: '-14px',
  top: 'calc(50% - 14px)',
  background: '#2A3139',
  boxShadow: '0px 3px 4px rgba(0, 0, 0, 0.25)',
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  width: '28px',
  height: '28px',
}));

// news box
const NewsBox = styled(Box)(({ theme }) => ({
  width: '100%',
  background: 'rgba(42, 49, 57, 0.4)',
  boxShadow:
      '0px 100px 77px rgba(0, 0, 0, 0.0), 0px 25.9935px 52.3711px rgba(0, 0, 0, 0.0), 0px 10.1608px 47.2956px rgba(0, 0, 0, 0.0), 0px 5.70356px 44.4605px rgba(0, 0, 0, 0.0), 0px 3.80638px 39.8596px rgba(0, 0, 0, 0.0), 0px 2.15748px 29.2214px rgba(0, 0, 0, 0.0)',
  borderRadius: '20px',
  padding: '20px 10px',

  [theme.breakpoints.up('xs')]: {
    padding: '0px',
    boxShadow: 'none',
  },
  [theme.breakpoints.up('md')]: {
    padding: '20px 10px',
    boxShadow:
        '0px 100px 77px rgba(0, 0, 0, 0.0), 0px 25.9935px 52.3711px rgba(0, 0, 0, 0.0), 0px 10.1608px 47.2956px rgba(0, 0, 0, 0.0), 0px 5.70356px 44.4605px rgba(0, 0, 0, 0.0), 0px 3.80638px 39.8596px rgba(0, 0, 0, 0.0), 0px 2.15748px 29.2214px rgba(0, 0, 0, 0.0)',
  },
}));
const NewsContent = styled(Box)(({ theme }) => ({
  position: 'relative',
  background: '#CCC292',
  borderRadius: '20px',
  padding: '15px',
  height: '160px',
  overflow: 'hidden',
}));

const NewsTitle = styled(Typography)(({ theme }) => ({
  position: 'relative',
  fontFamily: 'Prompt',
  fontStyle: 'normal',
  fontWeight: '600',
  color: 'rgba(39, 40, 42, 0.8)',
  fontSize: '16px',
  lineHeight: '18px',
  textAlign: 'center',
  zIndex: '2',
}));

const NewsSubTitle = styled(Typography)(({ theme }) => ({
  position: 'relative',
  fontFamily: 'Prompt',
  fontStyle: 'normal',
  fontWeight: '400',
  color: 'rgba(39, 40, 42, 0.6)',
  fontSize: '11px',
  lineHeight: 'normal',
  zIndex: '2',
}));

const NewsImg = styled('img')(({ theme }) => ({
  marginTop: '18px',

  [theme.breakpoints.up('xs')]: {
    width: 'calc(100% - 0px)',
    height: '80px',
    marginTop: '28px',
  },
  [theme.breakpoints.up('md')]: {
    marginTop: '18px',
  },
}));
const StampImg = styled('img')(({ theme }) => ({
  position: 'absolute',
  bottom: '3px',
  left: '16px',
  width: '210px',

  [theme.breakpoints.up('xs')]: {},
  [theme.breakpoints.up('md')]: {},
}));

const ToggleIcon = styled('img')(() => ({}));

const OverviewPage: FC = () => {
  const [isBurnAcceleratorExpanded, setBurnAcceleratorExpanded] = useState<boolean>(false);
  const [isBoilerRoomExpanded, setIsBoilerRoomExpanded] = useState<boolean>(false);
  const { selectedChainId } = useAppSelector((state) => state.chain);

  const { data: tokenList } = useAppSelector((state) => state.tokens);
  const { data: liquidityList } = useAppSelector((state) => state.liquidity);
  const [modalOpen, setModalOpen] = useState<boolean>(false);

  const basedTokenInfo = tokenList.find((token: any) => token.symbol === 'BASED');
  const bshareTokenInfo = tokenList.find((token: any) => token.symbol === 'BSHARE');
  const obolTokenInfo = tokenList.find((token: any) => token.symbol === 'OBOL');
  const smeltTokenInfo = tokenList.find((token: any) => token.symbol === 'SMELT');
  const dispatch = useAppDispatch();

  const overviewOptions = () => 
  {
    if( Number(selectedChainId) === Number(137) ){
      return  [
        { value: 'tokens', label: 'Tokens' },
        { value: 'liquidity', label: 'Liquidity' },
      ];
    }
    if( Number(selectedChainId) === Number(43114) ){
      return  [
        { value: 'tokens', label: 'Tokens' },
        { value: 'liquidity', label: 'Liquidity' },
      ];
    }
    if( Number(selectedChainId) !== Number(250) ){
      return  [
        { value: 'protocols', label: 'Protocols'},
        { value: 'tokens', label: 'Tokens' }
      ];
    }
    return  [
      { value: 'tokens', label: 'Tokens' },
      { value: 'liquidity', label: 'Liquidity' },
      // { value: 'protocols', label: 'Protocols'}
    ];
  }
  const [activeOverviewOption, setActiveOverviewOption] = useState<any>(overviewOptions()[0]);

  const onSelectOverviewOption = (overviewOption: any) => {
    if (overviewOption.value === activeOverviewOption.value) return;
    setActiveOverviewOption(overviewOption);
  };

  useEffect(() => {
    setActiveOverviewOption(overviewOptions()[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [selectedChainId]);

   useEffect(() => {
    if( activeOverviewOption.value === 'liquidity' ){
      dispatch(fetchLiquidityGlobalDataAsync(selectedChainId));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [activeOverviewOption]);

   const onCloseModal = () => {
    setModalOpen(false);
  };

  useEffect(() => {
    const a = document.createElement('a');
    const url = new URL(window.location.href);
    const res = url.pathname.split('/');

    if( res.length > 1 ){
        if( res[1] === "overview" ){
          dispatch(fetchTokenGlobalDataAsync(selectedChainId));
        }
      //  
      }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [window.location.href]);

  return (
    <>
    {modalOpen && <TutorialModal onClose={onCloseModal} />}
    <Box display='flex' sx={{height: '40px', position: 'absolute', zIndex: '2', width: '100%', background: '#BDE038',   border: '1px solid grey'}}>
{/*  */}
    <div className="wrap" >
      <div className="items-wrap" role="presentation" onClick={() => {window.open("https://launcher.basedfinance.io")}} onKeyDown={() => {window.open("https://launcher.basedfinance.io")}}>
        <div className="items marquee" >
          <div className="item" >
          NO-CODE TOKEN AND PRESALE LAUNCHER LIVE at Launcher.BasedFinance.io
          </div>
          <div className="item">
          NO-CODE TOKEN AND PRESALE LAUNCHER LIVE at Launcher.BasedFinance.io
          </div>
          <div className="item">
          NO-CODE TOKEN AND PRESALE LAUNCHER LIVE at Launcher.BasedFinance.io
          </div>
        </div>
        <div className="items marquee" aria-hidden="true">
        <div className="item">
        NO-CODE TOKEN AND PRESALE LAUNCHER LIVE at Launcher.BasedFinance.io
        </div>
          <div className="item">
          NO-CODE TOKEN AND PRESALE LAUNCHER LIVE at Launcher.BasedFinance.io
          </div>
          <div className="item">
          NO-CODE TOKEN AND PRESALE LAUNCHER LIVE at Launcher.BasedFinance.io
          </div>
        </div>
      </div>
    </div>

    </Box>
    <Container mt={5}>
      <Banner />
      <BannerHero/>
      {/* <Button title='Open' sx={{width: '100px', height: '100px', background: 'red'}} onClick={() => {setModalOpen(true)}} >
            Open
      </Button> */}
      <Content>
        {/* overview section */}
        <OverviewSection>
          <TvlCard />
          {Number(selectedChainId) !== 250  &&
            <>
            <NewsBox>
                <NewsContent>
                  <StampImg alt="stamp bg" src={icons.stamp} />
                  <Box sx={{ margin: 'auto', width: 'fit-content' }}>
                    <NewsTitle>LATEST NEWS BY BASED LABS</NewsTitle>
                    <NewsSubTitle>BASED</NewsSubTitle>
                  </Box>
                  <NewsImg alt="news" src={images.defaultNews} />
                </NewsContent>
              </NewsBox>
            </>
          }
          {Number(selectedChainId) === 250 &&
             <Grid container flex="1" flexWrap={{ xs: 'wrap', sm: 'nowrap' }} gap="20px" width="100%">
            <Grid item sm={6} xs={12}>
              <BurnBox>
                <ToggleSection
                  onClick={() => setBurnAcceleratorExpanded(!isBurnAcceleratorExpanded)}
                  sx={{
                    display: { xs: 'flex', sm: 'none' },
                  }}
                >
                  <ToggleIcon
                    alt="toggle"
                    src={icons.chevronDown}
                    sx={{ transform: isBurnAcceleratorExpanded ? 'rotate(180deg)' : 'rotate(0deg)' }}
                  />
                </ToggleSection>
                <BurnBoxTitle>Burn accelerator</BurnBoxTitle>
                <Grid container flex="1" flexWrap={{ xs: 'wrap', sm: 'nowrap' }} gap="12px" width="100%">
                  <Grid item sm={6} xs={12}>
                    <TokenBurnCard overflow='hidden' position="relative">
                      <Box display="flex">
                        <TokenBurnCardBasedBack/>
                        <TokenBurnCardImg alt="logo" src={tokens.token1} sx={{ zIndex: 1 }} />
                        <TokenBurnCardImg
                          alt="logo"
                          src={tokens.token2}
                          sx={{
                            marginLeft: '-10px',
                            display: { xs: !isBurnAcceleratorExpanded ? 'block' : 'none', sm: 'none' },
                          }}
                        />
                      </Box>
                      <TokenBurnAmount>
                        {isBurnAcceleratorExpanded
                          ? formatNumber(basedTokenInfo?.burntAmnt)
                          : formatNumber((basedTokenInfo?.burntAmnt || 0) + (bshareTokenInfo?.burntAmnt || 0))}
                      </TokenBurnAmount>
                    </TokenBurnCard>
                  </Grid>
                  <Grid display={{ xs: isBurnAcceleratorExpanded ? 'grid' : 'none', sm: 'grid' }} item sm={6} xs={12}>
                    <TokenBurnCard overflow='hidden' position="relative">
                    <TokenBurnCardBshareBack/>
                      <TokenBurnCardImg alt="logo" src={tokens.token2} />
                      <TokenBurnAmount>{formatNumber(bshareTokenInfo?.burntAmnt)}</TokenBurnAmount>
                    </TokenBurnCard>
                  </Grid>
                </Grid>
              </BurnBox>
            </Grid>
            <Grid item sm={6} sx={{ overflow: 'visible' }} xs={12}>
              <BurnBox>
                <ToggleSection
                  onClick={() => setIsBoilerRoomExpanded(!isBoilerRoomExpanded)}
                  sx={{
                    display: { xs: 'flex', sm: 'none' },
                  }}
                >
                  <ToggleIcon
                    alt="toggle"
                    src={icons.chevronDown}
                    sx={{ transform: isBoilerRoomExpanded ? 'rotate(180deg)' : 'rotate(0deg)' }}
                  />
                </ToggleSection>
                <BurnBoxTitle>Boiler Room</BurnBoxTitle>
                <Grid container flex="1" flexWrap={{ xs: 'wrap', sm: 'nowrap' }} gap="12px" width="100%">
                  <Grid item sm={6} xs={12}>
                    <TokenBurnCard overflow="hidden" position="relative">
                      <TokenBoilerRoomCardBack/>
                      <Box display="flex">
                        <TokenBurnCardImg alt="logo" src={tokens.token3} sx={{ zIndex: 1 }} />
                        <TokenBurnCardImg
                          alt="logo"
                          src={tokens.token4}
                          sx={{
                            marginLeft: '-10px',
                            display: { xs: !isBoilerRoomExpanded ? 'block' : 'none', sm: 'none' },
                          }}
                        />
                      </Box>
                      <TokenBurnAmount >
                        {isBoilerRoomExpanded
                          ? formatNumber(obolTokenInfo?.burntAmnt)
                          : formatNumber((obolTokenInfo?.burntAmnt || 0) + (smeltTokenInfo?.burntAmnt || 0))}
                      </TokenBurnAmount>
                    </TokenBurnCard>
                  </Grid>
                  <Grid display={{ xs: isBoilerRoomExpanded ? 'grid' : 'none', sm: 'grid' }} item sm={6} xs={12}>
                    <TokenBurnCard overflow="hidden" position="relative">
                      <TokenBoilerRoomCardBack/>
                      <TokenBurnCardImg alt="logo" src={tokens.token4} />
                      <TokenBurnAmount>{formatNumber(smeltTokenInfo?.burntAmnt)}</TokenBurnAmount>
                    </TokenBurnCard>
                  </Grid>
                </Grid>
              </BurnBox>
            </Grid>
          </Grid> 
        }
        {selectedChainId !== '250' &&
          <>
          </>
        }
        </OverviewSection>
        {/* token list section */}
        <TokenListSection>
        <Box justifyContent='center' mt={1} sx={{ display: { xs: 'block' }, border: '0px solid red' }}>
            <OverviewOptions
              activeOverviewOption={activeOverviewOption}
              options={overviewOptions()}
              selectOverviewOption={onSelectOverviewOption}
            />
        </Box>
        {activeOverviewOption.value === 'tokens' && 
          tokenList
            .filter((token: TokenInfo) => token.visible)
            .map((row: TokenInfo) => <TokenCard key={row.address} token={row} />)}
        {activeOverviewOption.value === 'liquidity' && 
          liquidityList
            .filter((lp: LiquidityInfo) => lp.visible)
            .map((row: LiquidityInfo) => <LiquidityCard key={row.address} lp={row} />)}
        {activeOverviewOption.value === 'protocols' &&
          <ProtocolChains/>}
        </TokenListSection>
      </Content>
    </Container>
    </>
  );
};

export default OverviewPage;
